import type { UndefinedOnPartialDeep } from 'type-fest';

import type { SelectedServices } from '@repo/api-adapters/selectors/services.selectors';
import { buildCompositeServiceId } from '@repo/api-adapters/services/services.api.utils';
import type { OrderAsset } from '@repo/types/order';
import {
    type ModifyDeliveryTimeWindowCommand,
    type ModifyProvidedServiceTimeWindowCommand,
    type OrderUpdateCommand,
    OrderUpdateCommandType,
} from '@repo/types/order-update';
import type { Service } from '@repo/types/service';
import type { TimeWindow } from '@repo/types/time-windows';

import { formatServiceTimeWindow } from '@/utils/services.utils';

interface PendingSubmissionSummary {
    id: string;
    label: string;
    before: string;
    after: string;
}

interface BuildSubmissionDataParams<Command extends OrderUpdateCommand = OrderUpdateCommand> {
    orderData: OrderAsset;
    services: SelectedServices;
    command: Command;
}

interface SubmissionData {
    label: string;
    isDelivery: Service['isDelivery'];
    beforeTimeWindow: UndefinedOnPartialDeep<TimeWindow> | undefined;
    afterTimeWindow: TimeWindow | undefined;
}

interface TimeWindowInput {
    fromDateTimeUtc: string | undefined;
    timeWindowId: string | undefined;
    toDateTimeUtc: string | undefined;
    timeZone: string | undefined;
}

/**
 * Creates a complete frontend time window, or returns undefined when required values are missing.
 */
const toTimeWindow = ({
    fromDateTimeUtc,
    timeWindowId,
    toDateTimeUtc,
    timeZone,
}: TimeWindowInput): TimeWindow | undefined => {
    if (!fromDateTimeUtc || !timeWindowId || !toDateTimeUtc || !timeZone) {
        return;
    }

    return {
        fromDateTimeUtc,
        timeWindowId,
        toDateTimeUtc,
        timeZone,
    };
};

/**
 * Formats a time window for the review modal, returning an empty value when incomplete.
 */
const formatTimeWindowValue = (
    isDelivery: Service['isDelivery'],
    timeWindow: UndefinedOnPartialDeep<TimeWindow> | undefined
): string => {
    const value = toTimeWindow({
        fromDateTimeUtc: timeWindow?.fromDateTimeUtc,
        timeWindowId: timeWindow?.timeWindowId,
        toDateTimeUtc: timeWindow?.toDateTimeUtc,
        timeZone: timeWindow?.timeZone,
    });

    return value ? formatServiceTimeWindow(isDelivery, value) : '';
};

/**
 * Builds the before and after values for a delivery time-window command.
 */
const buildModifyDeliveryTimeWindowSummaryData = ({
    services,
    command,
}: BuildSubmissionDataParams<ModifyDeliveryTimeWindowCommand>): SubmissionData => {
    const deliveryNo = String(command.deliveryNo);
    const service = services.find((candidate) => candidate.deliveryNo === deliveryNo);

    return {
        label: service?.serviceName ?? service?.serviceId ?? deliveryNo,
        isDelivery: true,
        beforeTimeWindow: service?.timeWindow,
        afterTimeWindow: toTimeWindow({
            fromDateTimeUtc: command.deliveryTimeWindow.deliveryDateTimeFrom,
            timeWindowId: command.deliveryTimeWindow.timeWindowId,
            toDateTimeUtc: command.deliveryTimeWindow.deliveryDateTimeTo,
            timeZone: command.deliveryTimeWindow.timeZoneId,
        }),
    };
};

/**
 * Builds the before and after values for a provided-service time-window command.
 */
const buildModifyProvidedServiceTimeWindowSummaryData = ({
    orderData,
    services,
    command,
}: BuildSubmissionDataParams<ModifyProvidedServiceTimeWindowCommand>): SubmissionData => {
    const providedService = orderData.assetDetails._embedded?.providedServices?.find(
        (service) => service.lineId === command.serviceNo
    );
    const serviceId = buildCompositeServiceId(providedService?.serviceItemType, providedService?.serviceItemNo);
    const service = services.find((candidate) => candidate.serviceId === serviceId);
    const serviceTimeWindow = command.serviceTimeWindows[0];
    const fallbackLabel = serviceId ?? String(command.serviceNo);

    return {
        label: service?.serviceName ?? service?.serviceId ?? fallbackLabel,
        isDelivery: false,
        beforeTimeWindow: service?.timeWindow,
        afterTimeWindow: toTimeWindow({
            fromDateTimeUtc: serviceTimeWindow?.serviceDateTimeFrom,
            timeWindowId: serviceTimeWindow?.timeWindowId,
            toDateTimeUtc: serviceTimeWindow?.serviceDateTimeTo,
            timeZone: serviceTimeWindow?.timeZoneId,
        }),
    };
};

/**
 * Builds the review summary for a supported order-update command, excluding unchanged commands.
 */
export const buildOrderUpdateCommandSummary = (
    params: BuildSubmissionDataParams
): Omit<PendingSubmissionSummary, 'id'> | undefined => {
    let data: SubmissionData;

    switch (params.command.type) {
        case OrderUpdateCommandType.ModifyDeliveryTimeWindow: {
            data = buildModifyDeliveryTimeWindowSummaryData({ ...params, command: params.command });
            break;
        }
        case OrderUpdateCommandType.ModifyProvidedServiceTimeWindow: {
            data = buildModifyProvidedServiceTimeWindowSummaryData({ ...params, command: params.command });
            break;
        }
    }

    const { label, isDelivery, beforeTimeWindow, afterTimeWindow } = data;
    const before = formatTimeWindowValue(isDelivery, beforeTimeWindow);
    const after = formatTimeWindowValue(isDelivery, afterTimeWindow);

    if (beforeTimeWindow?.timeWindowId === afterTimeWindow?.timeWindowId) {
        return;
    }

    return {
        label,
        before,
        after,
    };
};
